import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import "@/App.css";
import BottomNav from "@/components/BottomNav";
import GlobalMissionWatcher from "@/components/GlobalMissionWatcher";

// All pages lazy loaded — nothing is in the initial bundle except App shell
const Login = lazy(() => import("@/pages/Login"));
const Home = lazy(() => import("@/pages/Home"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Register = lazy(() => import("@/pages/Register"));
const OAuthCallback = lazy(() => import("@/pages/OAuthCallback"));
const Map = lazy(() => import("@/pages/Map"));
const PlaceDetail = lazy(() => import("@/pages/PlaceDetail"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const EntrepreneurPanel = lazy(() => import("@/pages/EntrepreneurPanel"));
const AsesorPanel = lazy(() => import("@/pages/AsesorPanel"));
const Profile = lazy(() => import("@/pages/Profile"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CategoryPlaces = lazy(() => import("@/pages/CategoryPlaces"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const RecommendedExcursion = lazy(() => import("@/pages/RecommendedExcursion"));
const PersonalizedExcursion = lazy(() => import("@/pages/PersonalizedExcursion"));
const SharedExcursion = lazy(() => import("@/pages/SharedExcursion"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const FeaturedByCategory = lazy(() => import("@/pages/FeaturedByCategory"));
const About = lazy(() => import("@/pages/About"));
const ExplorerProfile = lazy(() => import("@/pages/ExplorerProfile"));
const ExplorerBackpack = lazy(() => import("@/pages/ExplorerBackpack"));
const ScanQR = lazy(() => import("@/pages/ScanQR"));
const DiscoverPlace = lazy(() => import("@/pages/DiscoverPlace"));
const EntrepreneurRequest = lazy(() => import("@/pages/EntrepreneurRequest"));

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-background">
      {/* Skeleton header */}
      <div className="sticky top-0 z-40 bg-white border-b border-border h-20" />
      {/* Skeleton body */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div className="h-48 bg-muted rounded-3xl animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />)}
        </div>
      </div>
    </div>
  );
}

// Wrapper: show BottomNav only in public navigation pages (mobile only)
function BottomNavWrapper() {
  const location = useLocation();
  const pathname = location.pathname;

  // Show on these paths (mobile navigation surface)
  const showOn = [
    '/home',
    '/map',
    '/favorites',
    '/excursion',
    '/quienes-somos',
    '/featured-by-category',
    '/explorador',
    '/mochila',
    '/descubrir-lugar',
  ];
  const isCategory = pathname.startsWith('/category/');
  const isPlace = pathname.startsWith('/place/');
  const shouldShow = showOn.includes(pathname) || isCategory || isPlace;

  if (!shouldShow) return null;
  return <BottomNav />;
}

function getInitialAuthState() {
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');
  if (token && userData) {
    try {
      return { authenticated: true, user: JSON.parse(userData) };
    } catch { /* fall through */ }
  }
  return { authenticated: false, user: null };
}

function App() {
  const [initialAuth] = useState(getInitialAuthState);
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth.authenticated);
  const [isGuest, setIsGuest] = useState(() => localStorage.getItem('guestMode') === 'true');
  const [user, setUser] = useState(initialAuth.user);
  // profileLoaded: true if not authenticated, or after profile refresh completes
  const [profileLoaded, setProfileLoaded] = useState(!initialAuth.authenticated);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('hasSeenOnboarding');
  });
  const [userLocation, setUserLocation] = useState(() => {
    try {
      const saved = localStorage.getItem('userLocation');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  // Global axios 401 interceptor — logs out user if token is invalid/expired
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        const url = error.config?.url || '';
        // Don't intercept auth endpoints (login/register return 401 for wrong creds)
        if (error.response?.status === 401 && !url.includes('/auth/')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('guestMode');
          toast.error('Tu sesión expiró. Por favor iniciá sesión de nuevo.');
          setTimeout(() => { window.location.href = '/login'; }, 1500);
          return Promise.reject({ intercepted: true });
        }
        return Promise.reject(error);
      }
    );
    return () => { axios.interceptors.response.eject(interceptorId); };
  }, []);

  // Refresh user profile from server to get latest role
  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(res => {
            if (res.status === 401) {
              // Token is invalid/expired — force logout on app load
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('guestMode');
              setIsAuthenticated(false);
              setIsGuest(false);
              setUser(null);
              setProfileLoaded(true);
              toast.error('Tu sesión expiró. Por favor iniciá sesión de nuevo.');
              return null;
            }
            return res.ok ? res.json() : null;
          })
          .then(data => {
            if (data && data.role) {
              const updatedUser = { ...user, role: data.role, name: data.name || user?.name };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          })
          .catch(() => {})
          .finally(() => setProfileLoaded(true));
      } else {
        setProfileLoaded(true);
      }
    }
    if (!userLocation) {
      requestLocation();
    }
  }, [isAuthenticated]);

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          localStorage.setItem('userLocation', JSON.stringify(location));
          // Persist to backend to enable push notifications for nearby benefits
          const token = localStorage.getItem('token');
          if (token) {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            fetch(`${backendUrl}/api/explorer/location`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(location),
            }).catch(() => {});
          }
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('guestMode');
    setIsAuthenticated(true);
    setIsGuest(false);
    setUser(userData);
    setProfileLoaded(true);
  };

  const handleGuestAccess = () => {
    localStorage.setItem('guestMode', 'true');
    localStorage.setItem('hasSeenOnboarding', 'true');
    setIsGuest(true);
    setShowOnboarding(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('guestMode');
    setIsAuthenticated(false);
    setIsGuest(false);
    setUser(null);
  };

  const completeOnboarding = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };

  const canAccessApp = isAuthenticated || isGuest;

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
        <GlobalMissionWatcher isAuthenticated={isAuthenticated} />
        <Routes>
          <Route 
            path="/" 
            element={
              showOnboarding ? (
                <Onboarding onComplete={completeOnboarding} onGuestAccess={handleGuestAccess} />
              ) : canAccessApp ? (
                <Navigate to="/home" />
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/home" />
              ) : (
                <Login onLogin={handleLogin} onGuestAccess={handleGuestAccess} />
              )
            } 
          />
          <Route 
            path="/register" 
            element={
              isAuthenticated ? (
                <Navigate to="/home" />
              ) : (
                <Register onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="/oauth-callback" 
            element={<OAuthCallback onLogin={handleLogin} />} 
          />
          {/* Rutas públicas — acceso libre sin registro */}
          <Route path="/home" element={<Home user={user} userLocation={userLocation} isGuest={isGuest} />} />
          <Route path="/map" element={<Map user={user} userLocation={userLocation} />} />
          <Route path="/category/:categoryId" element={<CategoryPlaces />} />
          <Route path="/place/:id" element={<PlaceDetail user={user} />} />
          <Route path="/excursion" element={<RecommendedExcursion />} />
          {/* Excursión personalizada — requiere registro para guardar la ruta */}
          <Route
            path="/excursion/personalizar"
            element={
              isAuthenticated ? (
                <PersonalizedExcursion />
              ) : (
                <Navigate to="/login" state={{ message: 'Registrate para guardar tu excursión personalizada y que no se pierda.' }} />
              )
            }
          />
          {/* Paneles de gestión — solo para usuarios con rol */}
          <Route 
            path="/admin" 
            element={
              !isAuthenticated ? (
                <Navigate to="/login" />
              ) : !profileLoaded ? (
                <LoadingSpinner />
              ) : user?.role === 'admin' ? (
                <AdminPanel user={user} />
              ) : (
                <Navigate to="/home" />
              )
            } 
          />
          <Route 
            path="/entrepreneur" 
            element={
              !isAuthenticated ? (
                <Navigate to="/login" />
              ) : !profileLoaded ? (
                <LoadingSpinner />
              ) : user?.role === 'entrepreneur' ? (
                <EntrepreneurPanel user={user} />
              ) : (
                <Navigate to="/home" />
              )
            } 
          />
          <Route 
            path="/asesor" 
            element={
              !isAuthenticated ? (
                <Navigate to="/login" />
              ) : !profileLoaded ? (
                <LoadingSpinner />
              ) : user?.role === 'asesor' ? (
                <AsesorPanel user={user} />
              ) : (
                <Navigate to="/home" />
              )
            } 
          />
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? (
                <Profile user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route 
            path="/favorites" 
            element={
              isAuthenticated ? (
                <Favorites />
              ) : (
                <Navigate to="/login" state={{ message: 'Iniciá sesión para ver tus favoritos.' }} />
              )
            } 
          />
          <Route path="/suscripcion-exitosa" element={<SubscriptionSuccess />} />
          <Route path="/featured-by-category" element={<FeaturedByCategory userLocation={userLocation} />} />
          <Route path="/quienes-somos" element={<About />} />
          <Route
            path="/explorador"
            element={
              isAuthenticated ? (
                <ExplorerProfile />
              ) : (
                <Navigate to="/login" state={{ message: 'Iniciá sesión para acceder a tu Perfil de Explorador.' }} />
              )
            }
          />
          <Route
            path="/mochila"
            element={
              isAuthenticated ? (
                <ExplorerBackpack />
              ) : (
                <Navigate to="/login" state={{ message: 'Iniciá sesión para ver tu Mochila.' }} />
              )
            }
          />
          <Route
            path="/scan"
            element={
              isAuthenticated ? (
                <ScanQR />
              ) : (
                <Navigate to="/login" state={{ message: 'Iniciá sesión para escanear tesoros.' }} />
              )
            }
          />
          <Route
            path="/descubrir-lugar"
            element={
              isAuthenticated ? (
                <DiscoverPlace />
              ) : (
                <Navigate to="/login" state={{ message: 'Iniciá sesión para descubrir lugares.' }} />
              )
            }
          />
          <Route
            path="/solicitar-emprendedor"
            element={
              isAuthenticated ? (
                <EntrepreneurRequest />
              ) : (
                <Navigate to="/login" state={{ message: 'Iniciá sesión para solicitar ser emprendedor.' }} />
              )
            }
          />
          {/* Excursión compartida — acceso público sin login */}
          <Route path="/shared-excursion/:token" element={<SharedExcursion />} />
        </Routes>
        </Suspense>
        <BottomNavWrapper />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
